/* eslint-disable max-lines */

import { type PayloadAction, createAction, createReducer } from '@reduxjs/toolkit';
import type { FeedApiV4ResponseListItem } from 'reactApp/api/FeedAPICall';
import { IS_B2B_BIZ_USER } from 'reactApp/appHelpers/configHelpers';
import { CAMERA_UPLOADS_FOLDER_ID, DEFAULT_FILES_HASHES, ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { normalizeBaseFeedItem } from 'reactApp/modules/feed/feed.helpers';
import type { FeedItemApiResponse } from 'reactApp/modules/feed/feed.types';
import { isFolder, normalizeId } from 'reactApp/modules/file/utils';
import { DEFAULT_CATEGORY } from 'reactApp/modules/gallery/gallery.constants';
import { GALLERY_B2B_IMAGES_EXTENSIONS, GALLERY_B2B_VIDEO_EXTENSIONS } from 'reactApp/modules/gallery/gallery.data';
import { normalizeAnchorV4Item, normalizeFeedV4ItemToGallery } from 'reactApp/modules/gallery/gallery.helpers';
import {
    type ChangeGalleryFolderAction,
    type GalleryAnchor,
    type GalleryAnchorsLoadRequestAction,
    type GalleryAnchorsLoadSuccessAction,
    type GalleryAnchorsStatusAction,
    type GalleryCategory,
    type GalleryItem,
    type GalleryLoadAnchorListRequestAction,
    type GalleryLoadAnchorListSuccessAction,
    type GalleryLoadAnchorPaginatedListSuccessAction,
    type GalleryLoadCategoryRequestAction,
    type GalleryLoadMoreSuccessAction,
    type GalleryLoadSuccessAction,
    type GalleryState,
    GalleryCategoryType,
} from 'reactApp/modules/gallery/gallery.types';
import { isTheSameFile, processToggleDisablePublicDownload, processToggleSeo } from 'reactApp/modules/home/home.helpers';
import {
    addFilesSuccess,
    addToFavoritesSuccess,
    moveItemsSuccess,
    publishWeblink,
    removeFileSuccess,
    removeFromFavoritesSuccess,
    renameItemSuccess,
    resetWeblinkCountDownloads,
    toggleDisablePublicDownload,
    toggleSeo,
    toggleWeblinkAccessRights,
    toggleWeblinkDomestic,
    toggleWeblinkDownloadable,
    unPublishWeblink,
    updateWeblinkCountDownloads,
    updateWeblinkExpires,
    weblinkSetDomainAccess,
} from 'reactApp/modules/modifying/modifying.actions';
import {
    type AddFile,
    type AddFileSuccessAction,
    type IAddToFavoritesSuccess,
    type IRemoveFromFavoritesSuccess,
    type IRenameItemSuccess,
    type IWeblinkSetDomainAccess,
    type MoveItemsSuccessAction,
    type RemoveFileSuccessAction,
    type ResetWeblinkCountDownloads,
    type ToggleSeoData,
    type ToggleWeblinkAccessRights,
    type ToggleWeblinkDomestic,
    type ToggleWeblinkDownloadable,
    type UnpublishWeblinkAction,
    type UpdateWeblinkAction,
    type UpdateWeblinkCountDownloads,
    type UpdateWeblinkExpires,
    EModifyReason,
} from 'reactApp/modules/modifying/modifying.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { AccessRights } from 'reactApp/types/Tree';

const filterOutDefaultContentAdnFoldersItems = (item: FeedItemApiResponse) =>
    !isFolder(item) && !(item.hash && DEFAULT_FILES_HASHES.includes(item.hash));

// Проверяем что файл лежит строго в указанной папке, а не в подпапках.
const filterFilesInExactFolder = (item: FeedItemApiResponse | FeedApiV4ResponseListItem, isExact = false, folder?: string) => {
    if (!isExact || !folder || folder === ROOT_FOLDER_ID) {
        return true;
    }

    return item.home === `${folder}/${item.name}`;
};

const categoryBaseProps = {
    childs: [],
    anchors: [],
    targetAnchorId: null,
    activeAnchorId: null,
    cursor: '',
    isLoaded: false,
    isLoading: false,
    hasMoreToLoad: false,
};

const assemble_B2C_categories = (): GalleryCategory[] => [
    {
        name: 'Все фото и видео',
        id: GalleryCategoryType.all,
        value: GalleryCategoryType.all,
        ...categoryBaseProps,
    },
    {
        name: 'С автозагрузкой',
        id: GalleryCategoryType.mobile,
        value: GalleryCategoryType.mobile,
        ...categoryBaseProps,
    },
    {
        name: 'Вручную',
        id: GalleryCategoryType.desktop,
        value: GalleryCategoryType.desktop,
        ...categoryBaseProps,
    },
];

const assemble_B2B_categories = (): GalleryCategory[] => [
    {
        name: 'Изображения',
        id: GalleryCategoryType.images,
        value: GalleryCategoryType.images,
        ...categoryBaseProps,
    },
    {
        name: 'Видео',
        id: GalleryCategoryType.videos,
        value: GalleryCategoryType.videos,
        ...categoryBaseProps,
    },
    {
        name: 'Другие',
        id: GalleryCategoryType.all,
        value: GalleryCategoryType.all,
        ...categoryBaseProps,
    },
];

const initialState: GalleryState = {
    categories: IS_B2B_BIZ_USER ? assemble_B2B_categories() : assemble_B2C_categories(),
    list: {},
    anchors: {},
    targetAnchorId: null,
    activeAnchorId: null,
    currentCategory: DEFAULT_CATEGORY,
    currentFolder: undefined,
};

export const galleryLoadCategoryRequest = createAction<GalleryLoadCategoryRequestAction>('gallery/changeCategory');
export const galleryLoadSuccess = createAction<GalleryLoadSuccessAction>('gallery/loadSuccess');

export const galleryLoadAnchorListRequest = createAction<GalleryLoadAnchorListRequestAction>('gallery/galleryLoadAnchorListRequest');

export const galleryLoadAnchorPaginatedListSuccess = createAction<GalleryLoadAnchorPaginatedListSuccessAction>(
    'gallery/galleryLoadAnchorPaginatedListSuccess'
);
export const galleryLoadAnchorListSuccess = createAction<GalleryLoadAnchorListSuccessAction>('gallery/galleryLoadAnchorListSuccess');

export const galleryAnchorsRequest = createAction<GalleryAnchorsLoadRequestAction>('gallery/loadAnchors');
export const galleryAnchorsLoadSuccess = createAction<GalleryAnchorsLoadSuccessAction>('gallery/anchorsLoadSuccess');

export const galleryLoadMoreRequest = createAction('gallery/loadMoreRequest');
export const galleryLoadMoreSuccess = createAction<GalleryLoadMoreSuccessAction>('gallery/loadMoreSuccess');

export const changeGalleryFolder = createAction<ChangeGalleryFolderAction>('gallery/changeFolder');
export const resetGallery = createAction('gallery/resetCurrentFolder');

export const galleryAnchorsStatus = createAction<GalleryAnchorsStatusAction>('gallery/galleryAnchorsStatus');

export const galleryReducer = createReducer(initialState, {
    [galleryLoadSuccess.type]: (state, action: ReturnType<typeof galleryLoadSuccess>) => {
        const category = state.categories.find((category) => category.id === action.payload.category);
        const folder = action.payload.folder;
        const isExactFolder = action.payload.isExactFolder;

        if (!category) {
            return;
        }

        const {
            payload: { data },
        } = action;

        if (data.galleryListV2) {
            const list = data.galleryListV2;

            category.cursor = list.cursor || '';
            category.hasMoreToLoad = !!list.cursor;
            category.childs = [];
            list.objects
                ?.filter(filterOutDefaultContentAdnFoldersItems)
                .filter((item) => filterFilesInExactFolder(item, isExactFolder, folder))
                .forEach((item) => {
                    state.list[item.home] = { ...normalizeBaseFeedItem(item), storage: EStorageType.gallery };
                    category.childs.push(item.home);
                });
        } else if (data.galleryListV4) {
            const list = data.galleryListV4;

            category.cursor = list.cursor || '';
            category.hasMoreToLoad = !!list.cursor;
            category.childs = [];
            list.list
                ?.filter((item) => filterFilesInExactFolder(item, isExactFolder, folder))
                .forEach((item) => {
                    state.list[item.home] = { ...normalizeFeedV4ItemToGallery(item), storage: EStorageType.gallery };
                    category.childs.push(item.home);
                });
        }

        category.isLoaded = true;
        category.isLoading = false;
        state.currentCategory = action.payload.category;

        if (folder) {
            state.currentFolder = folder;
        }
    },
    [galleryLoadAnchorListRequest.type]: (state, action: ReturnType<typeof galleryLoadAnchorListRequest>) => {
        const { anchorId } = action.payload;

        const anchor = state.anchors[anchorId];
        if (!anchor) {
            return;
        }

        anchor.isLoaded = false;
        anchor.isLoading = true;
    },
    [galleryLoadAnchorPaginatedListSuccess.type]: (state, action: ReturnType<typeof galleryLoadAnchorPaginatedListSuccess>) => {
        const { data, anchorId, cursorOrder } = action.payload;

        const anchor = state.anchors[anchorId];
        if (!anchor) {
            return;
        }

        anchor.cursor = data.cursor || '';
        anchor.cursorOrder = cursorOrder;
        /** Якорь загружен, только если нет курсора */
        anchor.isLoaded = !anchor.cursor;
        anchor.isLoading = false;

        const currentCategory = state.currentCategory;
        const category = state.categories.find((category) => category.id === currentCategory);

        if (!category) {
            return;
        }

        data.list?.forEach((item) => {
            state.list[item.home] = { ...normalizeFeedV4ItemToGallery(item), storage: EStorageType.gallery };
            category.childs.push(item.home);
        });
    },
    [galleryLoadAnchorListSuccess.type]: (state, action: ReturnType<typeof galleryLoadAnchorListSuccess>) => {
        const { data, loadedAnchorIds } = action.payload;

        loadedAnchorIds.forEach((anchorId) => {
            const anchor = state.anchors[anchorId];
            if (!anchor) {
                return;
            }

            anchor.isLoaded = true;
            anchor.isLoading = false;
        });

        const currentCategory = state.currentCategory;
        const category = state.categories.find((category) => category.id === currentCategory);

        if (!category) {
            return;
        }

        data.list?.forEach((item) => {
            state.list[item.home] = { ...normalizeFeedV4ItemToGallery(item), storage: EStorageType.gallery };
            category.childs.push(item.home);
        });
    },
    [galleryLoadCategoryRequest.type]: (state, action: ReturnType<typeof galleryLoadCategoryRequest>) => {
        state.currentCategory = action.payload.category;

        const category = state.categories.find((category) => category.id === action.payload.category);

        if (category) {
            category.cursor = '';
            category.isLoaded = false;
            category.isLoading = true;
        }
    },
    [toggleWeblinkDomestic.type]: (state, action: PayloadAction<ToggleWeblinkDomestic>): void => {
        const { id, domestic } = action.payload;

        if (state.list[id]) {
            state.list[id].weblinkDomestic = domestic;
        }
    },
    [weblinkSetDomainAccess.type]: (state, action: PayloadAction<IWeblinkSetDomainAccess>): void => {
        const { domain, itemId } = action.payload;

        if (state.list[itemId]) {
            state.list[itemId].weblinkDomainAccess = domain;
        }
    },
    [updateWeblinkExpires.type]: (state, action: PayloadAction<UpdateWeblinkExpires>): void => {
        const { id, expires } = action.payload;

        if (state.list[id]) {
            state.list[id].weblinkExpires = expires;
        }
    },
    [publishWeblink.type]: (state, action: PayloadAction<UpdateWeblinkAction>) => {
        const { id, weblink, uniqueId } = action.payload;

        if (state.list[id]) {
            state.list[id].weblink = weblink;
            state.list[id].uniqueId = uniqueId;
        }
    },
    [toggleWeblinkAccessRights.type]: (state, action: PayloadAction<ToggleWeblinkAccessRights>): void => {
        const { id, type } = action.payload;
        if (state.list[id]) {
            state.list[id].weblinkAccessRights = type;
        }
    },
    [toggleWeblinkDownloadable.type]: (state, action: PayloadAction<ToggleWeblinkDownloadable>): void => {
        const { id, downloadable } = action.payload;

        if (state.list[id]) {
            state.list[id].weblinkDownloadable = downloadable;
        }
    },
    [updateWeblinkCountDownloads.type]: (state, action: PayloadAction<UpdateWeblinkCountDownloads>): void => {
        const { id, count_downloads } = action.payload;
        const item = state.list[id];
        if (item) {
            item.count_downloads_left = count_downloads;
            item.count_downloads_total = count_downloads;
        }
    },
    [resetWeblinkCountDownloads.type]: (state, action: PayloadAction<ResetWeblinkCountDownloads>): void => {
        const { id } = action.payload;

        if (state.list[id]) {
            state.list[id].count_downloads_total = undefined;
            state.list[id].count_downloads_left = undefined;
        }
    },
    [unPublishWeblink.type]: (state, action: PayloadAction<UnpublishWeblinkAction>) => {
        const { ids } = action.payload;

        ids.forEach((id) => {
            const item = state.list[id];

            if (!item) {
                return;
            }

            item.weblink = undefined;
            item.weblinkAccessRights = AccessRights.read;
            item.weblinkDomestic = false;
            item.weblinkExpires = 0;

            if (item.isFolder) {
                item.weblinkAutoDelete = false;
            }
        });
    },
    [toggleSeo.type]: (state, action: PayloadAction<ToggleSeoData>): void => {
        processToggleSeo<GalleryItem>(state.list[action.payload.item.id]);
    },
    [toggleDisablePublicDownload.type]: (state, action: ReturnType<typeof toggleDisablePublicDownload>): void => {
        processToggleDisablePublicDownload<GalleryItem>(state.list[action.payload.item.id], action.payload.disableDownload);
    },
    [removeFileSuccess.type]: (state, action: PayloadAction<RemoveFileSuccessAction>) => {
        const { ids, reason, descriptors } = action.payload;

        if (reason === EModifyReason.move) {
            return;
        }

        ids.forEach((id) => {
            if ((descriptors && isTheSameFile(state.list[id], descriptors[id])) || !descriptors) {
                delete state.list[id];
            }
        });

        const category = state.categories.find((category) => category.id === state.currentCategory);

        if (category) {
            category.childs = category.childs.filter((child) => state.list[child]);
        }
    },
    [addFilesSuccess.type]: (state, action: PayloadAction<AddFileSuccessAction>) => {
        const { items, storage, reason } = action.payload;
        const category = state.categories.find((category) => category.id === state.currentCategory);

        if (reason === EModifyReason.move || !category || category?.id === GalleryCategoryType.mobile || storage !== EStorageType.gallery) {
            return;
        }

        const files = items as AddFile[];

        files.forEach((file) => {
            if (!file.home || file.kind === 'folder') {
                return;
            }

            const name = normalizeId(file.home).split('/').pop();
            const normalizedItem = normalizeBaseFeedItem({ ...file, name, favorite: false, home: file.home });

            if (
                (state.currentCategory === GalleryCategoryType.mobile && file.home.startsWith(CAMERA_UPLOADS_FOLDER_ID)) ||
                (state.currentCategory === GalleryCategoryType.desktop && !file.home.startsWith(CAMERA_UPLOADS_FOLDER_ID)) ||
                (state.currentCategory === GalleryCategoryType.images && GALLERY_B2B_IMAGES_EXTENSIONS.includes(normalizedItem.ext)) ||
                (state.currentCategory === GalleryCategoryType.videos && GALLERY_B2B_VIDEO_EXTENSIONS.includes(normalizedItem.ext)) ||
                state.currentCategory === GalleryCategoryType.all
            ) {
                state.list[file.home] = {
                    ...normalizedItem,
                    storage: EStorageType.gallery,
                };
                state.list[file.home].mtime = state.list[file.home].mtime / 1000;
                category.childs = [file.home, ...category.childs];
            }
        });
    },
    [moveItemsSuccess.type]: (state, action: PayloadAction<MoveItemsSuccessAction>) => {
        const { newItems, oldIds, storage } = action.payload;

        const category = state.categories.find((category) => category.id === state.currentCategory);

        if (!category || storage !== EStorageType.gallery) {
            return;
        }

        const isMobile = state.currentCategory === GalleryCategoryType.mobile;
        const isDesktop = state.currentCategory === GalleryCategoryType.desktop;
        const isAll = state.currentCategory === GalleryCategoryType.all;
        const isImages = state.currentCategory === GalleryCategoryType.images;
        const isVideos = state.currentCategory === GalleryCategoryType.videos;

        newItems.forEach((newItem, index) => {
            /**
             * При перемещении через истории у файла остается storage = story, что ломает рендер DataListGalleryItem
             * Явно меняем на gallery
             */
            if (newItem.storage === EStorageType.story) {
                newItem.storage = EStorageType.gallery;
            }

            const file = newItem as GalleryItem;
            const oldItemId = oldIds[index];
            const oldItem = state.list[oldItemId];

            if (!file.home || !oldItem) {
                return;
            }

            const cameraUploadsFolder = file.home.startsWith(CAMERA_UPLOADS_FOLDER_ID);
            const currentCategory =
                (isMobile && cameraUploadsFolder) || (isDesktop && !cameraUploadsFolder) || isImages || isVideos || isAll;

            if (!currentCategory) {
                delete state.list[oldItemId];
                category.childs = category.childs.filter((childId) => childId !== oldItemId);

                return;
            }

            state.list[file.home] = file;
            const oldIdx = category.childs.indexOf(oldItemId);

            if (oldIdx !== -1) {
                category.childs[oldIdx] = file.home;
                category.childs = [...category.childs];
            } else {
                category.childs = [file.home, ...category.childs];
            }
            delete state.list[oldItemId];
        });
    },
    [renameItemSuccess.type]: (state, action: PayloadAction<IRenameItemSuccess>) => {
        const { newItem, oldId } = action.payload;
        const file = newItem as GalleryItem;

        if (!file.home || !oldId) {
            return;
        }

        const category = state.categories.find((category) => category.id === state.currentCategory);

        if (!category) {
            return;
        }

        state.list[file.home] = newItem as GalleryItem;
        delete state.list[oldId];

        category.childs = category.childs.filter((id) => id !== oldId);
        category.childs = [file.home, ...category.childs];
    },
    [galleryLoadMoreRequest.type]: (state) => {
        const category = state.categories.find((category) => category.id === state.currentCategory);

        if (category) {
            category.isLoading = true;
            category.isLoaded = false;
        }
    },
    [galleryLoadMoreSuccess.type]: (state, action: ReturnType<typeof galleryLoadMoreSuccess>) => {
        const currentCategory = action.payload.category || state.currentCategory;
        const category = state.categories.find((category) => category.id === currentCategory);

        const folder = action.payload.folder;
        const isExactFolder = action.payload.isExactFolder;

        if (!category) {
            return;
        }

        const {
            payload: { data },
        } = action;

        category.isLoading = false;
        category.isLoaded = true;

        if (data.galleryListV2) {
            const list = data.galleryListV2;

            category.cursor = list.cursor || '';
            category.hasMoreToLoad = !!list.cursor;

            list.objects
                ?.filter(filterOutDefaultContentAdnFoldersItems)
                .filter((item) => filterFilesInExactFolder(item, isExactFolder, folder))
                .forEach((item) => {
                    state.list[item.home] = { ...normalizeBaseFeedItem(item), storage: EStorageType.gallery };
                    category.childs.push(item.home);
                });
        } else if (data.galleryListV4) {
            const list = data.galleryListV4;

            category.cursor = list.cursor || '';
            category.hasMoreToLoad = !!list.cursor;

            list.list
                ?.filter((item) => filterFilesInExactFolder(item, isExactFolder, folder))
                .forEach((item) => {
                    state.list[item.home] = { ...normalizeFeedV4ItemToGallery(item), storage: EStorageType.gallery };
                    category.childs.push(item.home);
                });
        }
    },
    [addToFavoritesSuccess.type]: (state, action: PayloadAction<IAddToFavoritesSuccess>): void => {
        const { ids } = action.payload;

        ids.forEach((id) => {
            const item = state.list[id];

            if (!item) {
                return;
            }

            item.isInFavorites = true;
        });
    },
    [removeFromFavoritesSuccess.type]: (state, action: PayloadAction<IRemoveFromFavoritesSuccess>): void => {
        const { ids } = action.payload;

        ids.forEach((id) => {
            const item = state.list[id];

            if (!item) {
                return;
            }

            item.isInFavorites = false;
        });
    },
    [resetGallery.type]: (state): void => {
        state.categories = IS_B2B_BIZ_USER ? assemble_B2B_categories() : assemble_B2C_categories();
        state.list = {};
        state.anchors = {};
        state.targetAnchorId = null;
        state.activeAnchorId = null;
        state.currentCategory = GalleryCategoryType.all;
        state.currentFolder = undefined;
    },
    [galleryAnchorsRequest.type]: (state, action: ReturnType<typeof galleryAnchorsRequest>) => {
        state.currentCategory = action.payload.category;

        const category = state.categories.find((category) => category.id === action.payload.category);

        if (category) {
            category.anchors = [];
            category.isLoaded = false;
            category.isLoading = true;
        }
    },
    [galleryAnchorsLoadSuccess.type]: (state, action: ReturnType<typeof galleryAnchorsLoadSuccess>): void => {
        const { anchors } = action.payload;

        const mappedAnchors: Record<string, GalleryAnchor> = {};
        const category = state.categories.find((category) => category.id === state.currentCategory);

        if (!category) {
            return;
        }

        category.isLoaded = true;
        category.isLoading = false;

        anchors.forEach((anchor) => {
            const normalizedAnchor = normalizeAnchorV4Item(anchor);
            mappedAnchors[normalizedAnchor.id] = normalizedAnchor;
            category.anchors.push(normalizedAnchor.id);
        });

        state.anchors = mappedAnchors;
    },
    [galleryAnchorsStatus.type]: (state, action: ReturnType<typeof galleryAnchorsStatus>) => {
        state.targetAnchorId = action.payload.targetAnchorId;
        state.activeAnchorId = action.payload.activeAnchorId;
    },
});
